import { useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { EASE, MaskedLines, Reveal } from "../Reveal";

const API = `https://women-health-node-server.onrender.com/api`;

const STEPS = [
    {
        id: "age_group",
        q: "Your age group",
        hint: "Pick one",
        type: "single",
        options: ["18–24", "25–34", "35–44", "45–54", "55+", "Prefer not to say"],
    },
    {
        id: "journey",
        q: "Which best describes your current health journey?",
        hint: "Select all that apply",
        type: "multi",
        options: [
            "Period / menstrual health",
            "PCOS / hormonal health",
            "Fertility / trying to conceive",
            "Pregnancy / postpartum",
            "Perimenopause",
            "Menopause",
            "General women's health",
            "I'm not sure — I just know something feels different",
        ],
    },
    {
        id: "challenges",
        q: "What is the biggest health challenge you're facing right now?",
        hint: "Select all that apply",
        type: "multi",
        options: [
            "Irregular periods",
            "PCOS symptoms",
            "Weight / metabolic health",
            "Hormonal symptoms",
            "Fertility",
            "Postpartum changes",
            "Mood / stress / sleep",
            "Hot flashes / night sweats",
            "Menopause symptoms",
            "Understanding health reports",
            "Finding the right doctor",
            "Nutrition / lifestyle",
            "Other",
        ],
        otherKey: "challenges_other",
    },
    {
        id: "managing",
        q: "How are you currently managing this?",
        hint: "Select all that apply",
        type: "multi",
        options: [
            "Gynecologist / doctor",
            "Other healthcare professional",
            "Medication",
            "Diet / nutrition changes",
            "Exercise",
            "Another health app",
            "Google / social media",
            "Ayurveda / traditional healthcare",
            "I'm not currently doing anything",
            "Other",
        ],
        otherKey: "managing_other",
    },
    {
        id: "help_with",
        q: "What would you most want Femipal to help you with?",
        hint: "Select up to 3",
        type: "multi",
        max: 3,
        options: [
            "Understand my symptoms",
            "Track my periods / symptoms",
            "Understand my health reports",
            "Consult a gynecologist",
            "Get personalized nutrition support",
            "Manage PCOS / hormonal health",
            "Get postpartum support",
            "Navigate perimenopause / menopause",
            "Keep my health information organized",
            "Get ongoing support rather than one-time consultations",
            "Something else",
        ],
        otherKey: "help_other",
        otherTrigger: "Something else",
    },
    {
        id: "paid_before",
        q: "Have you ever paid for healthcare or a wellness program specifically related to this concern?",
        hint: "Pick one",
        type: "single",
        options: ["Yes", "No"],
    },
    {
        id: "spend_range",
        q: "Approximately how much have you spent?",
        hint: "Pick one",
        type: "single",
        when: (a) => a.paid_before === "Yes",
        options: ["Under ₹500", "₹500–₹2,000", "₹2,000–₹5,000", "₹5,000–₹10,000", "More than ₹10,000", "Prefer not to say"],
    },
    {
        id: "service",
        q: "Which Femipal service would you be most likely to pay for?",
        hint: "Select all that apply",
        type: "multi",
        options: [
            "Doctor consultation",
            "Personalized PCOS/hormonal-health program",
            "Nutrition/diet support",
            "Health-report interpretation/support",
            "Comprehensive women's-health membership",
            "I'm not sure yet",
            "I wouldn't pay for a digital health service",
        ],
    },
    {
        id: "trust",
        q: "What would make you trust a women's-health app?",
        hint: "Select up to 3",
        type: "multi",
        max: 3,
        options: [
            "Qualified doctors",
            "Clinical/medical oversight",
            "Privacy of my health information",
            "Personalized recommendations",
            "Easy access to specialists",
            "Evidence-based information",
            "Ability to keep my health records in one place",
            "Recommendations based on my own health history",
            "Other",
        ],
        otherKey: "trust_other",
    },
    {
        id: "one_problem",
        q: "If Femipal could solve one problem for you, what would you want it to solve?",
        hint: "A sentence or two is plenty",
        type: "text",
    },
    {
        id: "founding_interest",
        q: "Would you like to be part of the Femipal Founding 100 and test the app before/around launch?",
        hint: "Pick one",
        type: "single",
        options: ["Yes, I'd love to", "Maybe — I'd like to know more", "No, but I'd like to follow Femipal"],
    },
    { id: "contact", q: "Almost done — where can we reach you?", hint: "We'll only use this for Femipal updates", type: "contact" },
];

const pad = (n) => String(n).padStart(2, "0");

function Chip({ selected, onClick, children, testId }) {
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testId}
            className={`text-left px-4 py-3 sm:px-5 sm:py-3.5 rounded-sm border text-xs sm:text-sm md:text-base transition-[background-color,border-color,color] duration-300 flex items-center justify-between gap-3 w-full sm:w-auto ${selected
                    ? "bg-[#F3698F] border-[#F3698F] text-white"
                    : "bg-transparent hairline text-[#D1C6DF] hover:border-[#F3698F]/60 hover:text-white"
                }`}
        >
            <span className="leading-snug">{children}</span>
            {selected && <Check className="w-4 h-4 shrink-0" />}
        </button>
    );
}

export default function FoundingForm() {
    const [stepIdx, setStepIdx] = useState(0);
    const [answers, setAnswers] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    const steps = useMemo(() => STEPS.filter((s) => !s.when || s.when(answers)), [answers]);
    const step = steps[Math.min(stepIdx, steps.length - 1)];
    const isLast = stepIdx === steps.length - 1;

    const setSingle = (id, value) => setAnswers((a) => ({ ...a, [id]: value }));
    const toggleMulti = (s, value) => {
        setAnswers((a) => {
            const cur = a[s.id] || [];
            if (cur.includes(value)) return { ...a, [s.id]: cur.filter((v) => v !== value) };
            if (s.max && cur.length >= s.max) {
                toast.error(`Select up to ${s.max}`);
                return a;
            }
            return { ...a, [s.id]: [...cur, value] };
        });
    };

    const valid = () => {
        if (step.type === "single") return !!answers[step.id];
        if (step.type === "multi") return (answers[step.id] || []).length > 0;
        if (step.type === "text") return true;
        if (step.type === "contact") {
            const okContact = (answers.email || "").trim() || (answers.whatsapp || "").trim();
            return (answers.name || "").trim() && okContact && answers.consent;
        }
        return true;
    };

    const next = async () => {
        if (!valid()) {
            toast.error(
                step.type === "contact"
                    ? "We need your name, one way to reach you, and your consent."
                    : "Pick at least one option to continue."
            );
            return;
        }
        if (!isLast) {
            setStepIdx((i) => i + 1);
            return;
        }
        setSubmitting(true);
        try {
            const res = await axios.post(`${API}/founding-signup`, answers);
            if (res.data?.status === "ok") {
                setDone(true);
                window.__lenis?.scrollTo("#founding-form", { offset: -40, duration: 1.2 });
            } else {
                toast.error(res.data?.message || "Something went wrong. Try again.");
            }
        } catch {
            toast.error("Couldn't reach the server. Try again in a moment.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section id="founding-form" className="py-12 md:py-32 lg:py-40 border-t hairline" data-testid="founding-form-section">
            <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-10 grid grid-cols-12 gap-8 lg:gap-10">
                <div className="col-span-12 lg:col-span-4">
                    <Reveal>
                        <p className="text-xs sm:text-sm md:text-[16px] uppercase tracking-[0.1em] font-bold text-[#F3698F] mb-3 md:mb-5">
                            06 — The 1-minute form
                        </p>
                        <h2 className="font-serif-display font-light tracking-tight leading-[1.1] md:leading-[1.05] text-3xl sm:text-4xl md:text-5xl">
                            One minute. <em className="italic text-[#D1C6DF]">Zero medical history.</em>
                        </h2>
                        <p className="text-xs sm:text-sm md:text-base text-[#D1C6DF] leading-relaxed mt-4 md:mt-6 max-w-sm">
                            No reports, no hormone levels, no diagnoses. Just you telling us where you are —
                            so we can build Femipal around real women, not assumptions.
                        </p>
                    </Reveal>
                </div>

                <div className="col-span-12 lg:col-span-8">
                    <Reveal delay={0.1}>
                        <div className="border hairline rounded-sm bg-[#1E1C22] p-5 sm:p-8 md:p-12 min-h-[420px] md:min-h-[480px] flex flex-col" data-testid="founding-form-card">
                            {done ? (
                                <div className="flex-1 flex flex-col justify-center py-6" data-testid="form-success">
                                    <p className="overline-label text-[#F3698F] mb-4 md:mb-6">Thank you</p>
                                    <MaskedLines
                                        className="font-serif-display font-light tracking-tight leading-[1.05] text-4xl sm:text-5xl md:text-7xl"
                                        lines={[<span key="a">You're <em className="italic text-[#F3698F]">in.</em></span>]}
                                    />
                                    <motion.p
                                        className="text-sm sm:text-base md:text-lg text-[#D1C6DF] leading-relaxed max-w-md mt-6 md:mt-8"
                                        initial={{ opacity: 0, y: 16 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.8, ease: EASE, delay: 0.5 }}
                                    >
                                        Welcome to the Founding 100. We'll reach out on WhatsApp and email as the beta opens.
                                    </motion.p>
                                </div>
                            ) : (
                                <>
                                    <div className="flex items-center justify-between mb-6 md:mb-10 gap-2">
                                        <span className="overline-label text-xs sm:text-sm text-[#FFFFFF] shrink-0" data-testid="form-progress-label">
                                            {pad(stepIdx + 1)} / {pad(steps.length)}
                                        </span>
                                        <div className="flex-1 mx-3 sm:mx-6 h-px bg-[#3B3051] relative overflow-hidden">
                                            <motion.div
                                                className="absolute left-0 top-0 h-full bg-[#F3698F]"
                                                animate={{ width: `${((stepIdx + 1) / steps.length) * 100}%` }}
                                                transition={{ duration: 0.6, ease: EASE }}
                                                data-testid="form-progress-bar"
                                            />
                                        </div>
                                        <span className="overline-label text-[11px] sm:text-xs text-[#FFFFFF] text-right truncate max-w-[120px] sm:max-w-none">
                                            {step.hint}
                                        </span>
                                    </div>

                                    <AnimatePresence mode="wait">
                                        <motion.div
                                            key={step.id}
                                            initial={{ opacity: 0, x: 20 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            exit={{ opacity: 0, x: -20 }}
                                            transition={{ duration: 0.35, ease: EASE }}
                                            className="flex-1"
                                            data-testid={`form-step-${step.id}`}
                                        >
                                            <h3 className="font-serif-display font-light tracking-tight text-xl sm:text-2xl md:text-4xl leading-snug md:leading-tight mb-6 md:mb-8 max-w-2xl">
                                                {step.q}
                                            </h3>

                                            {(step.type === "single" || step.type === "multi") && (
                                                <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2.5 sm:gap-3">
                                                    {step.options.map((opt, i) => {
                                                        const selected =
                                                            step.type === "single"
                                                                ? answers[step.id] === opt
                                                                : (answers[step.id] || []).includes(opt);
                                                        return (
                                                            <Chip
                                                                key={opt}
                                                                testId={`form-option-${step.id}-${i}`}
                                                                selected={selected}
                                                                onClick={() =>
                                                                    step.type === "single" ? setSingle(step.id, opt) : toggleMulti(step, opt)
                                                                }
                                                            >
                                                                {opt}
                                                            </Chip>
                                                        );
                                                    })}
                                                </div>
                                            )}

                                            {step.otherKey && (answers[step.id] || []).some((o) => o === "Other" || o === "Something else") && (
                                                <input
                                                    type="text"
                                                    value={answers[step.otherKey] || ""}
                                                    onChange={(e) => setSingle(step.otherKey, e.target.value)}
                                                    placeholder="Tell us in a few words…"
                                                    data-testid={`form-other-input-${step.id}`}
                                                    className="mt-4 md:mt-5 w-full max-w-md bg-transparent border-b hairline focus:border-[#F3698F] outline-none py-2.5 md:py-3 text-sm md:text-base placeholder:text-[#8F82A2] transition-colors duration-300"
                                                />
                                            )}

                                            {step.type === "text" && (
                                                <textarea
                                                    rows={3}
                                                    value={answers.one_problem || ""}
                                                    onChange={(e) => setSingle("one_problem", e.target.value)}
                                                    placeholder="One or two sentences…"
                                                    data-testid="form-one-problem-input"
                                                    className="w-full bg-transparent border hairline rounded-sm focus:border-[#F3698F] outline-none p-4 sm:p-5 text-sm md:text-base placeholder:text-[#8F82A2] transition-colors duration-300 resize-none"
                                                />
                                            )}

                                            {step.type === "contact" && (
                                                <div className="space-y-4 sm:space-y-6 max-w-md">
                                                    {[
                                                        { id: "name", label: "Name", type: "text", ph: "Your name" },
                                                        { id: "whatsapp", label: "WhatsApp number", type: "tel", ph: "+91 …" },
                                                        { id: "email", label: "Email", type: "email", ph: "you@example.com" },
                                                    ].map((f) => (
                                                        <div key={f.id}>
                                                            <label className="overline-label text-[#D1C6DF] text-xs block mb-1.5">{f.label}</label>
                                                            <input
                                                                type={f.type}
                                                                value={answers[f.id] || ""}
                                                                onChange={(e) => setSingle(f.id, e.target.value)}
                                                                placeholder={f.ph}
                                                                data-testid={`form-contact-${f.id}`}
                                                                className="w-full bg-transparent border-b hairline focus:border-[#F3698F] outline-none py-2 md:py-3 text-sm md:text-base placeholder:text-[#8F82A2] transition-colors duration-300"
                                                            />
                                                        </div>
                                                    ))}
                                                    <label className="flex items-start gap-3 cursor-pointer pt-1" data-testid="form-consent-label">
                                                        <input
                                                            type="checkbox"
                                                            checked={!!answers.consent}
                                                            onChange={(e) => setSingle("consent", e.target.checked)}
                                                            data-testid="form-consent-checkbox"
                                                            className="mt-0.5 w-4 h-4 accent-[#F3698F] shrink-0"
                                                        />
                                                        <span className="text-xs sm:text-sm text-[#D1C6DF] leading-relaxed">
                                                            I agree to be contacted by Femipal regarding the beta, launch, feedback and relevant Femipal updates.
                                                        </span>
                                                    </label>
                                                </div>
                                            )}
                                        </motion.div>
                                    </AnimatePresence>

                                    <div className="flex items-center justify-between mt-8 md:mt-12 pt-6 md:pt-8 border-t hairline gap-4">
                                        <button
                                            type="button"
                                            onClick={() => setStepIdx((i) => Math.max(0, i - 1))}
                                            disabled={stepIdx === 0}
                                            data-testid="form-back-button"
                                            className="overline-label text-xs sm:text-sm text-[#D1C6DF] hover:text-white disabled:opacity-0 transition-colors duration-300 flex items-center gap-2 py-2"
                                        >
                                            <ArrowLeft className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> Back
                                        </button>
                                        <button
                                            type="button"
                                            onClick={next}
                                            disabled={submitting}
                                            data-testid="form-continue-button"
                                            className="group overline-label text-xs sm:text-sm bg-[#F3698F] text-white px-5 sm:px-7 py-3 sm:py-3.5 rounded-full hover:bg-[#E55C7E] disabled:opacity-50 transition-colors duration-300 flex items-center gap-2 sm:gap-3"
                                        >
                                            {submitting ? "Sending…" : isLast ? "Join the Founding 100" : "Continue"}
                                            {!submitting && <ArrowRight className="w-3.5 h-3.5 sm:w-4 sm:h-4 group-hover:translate-x-1 transition-transform duration-300" />}
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    </Reveal>
                </div>
            </div>
        </section>
    );
}